/* eslint-disable no-console */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../ui/Button';

interface TemporaryParticipantData {
  name: string;
  preferredName?: string; // Only for child - nickname/preferred name
  birthYear?: string; // Only for child
  birthMonth?: string; // Only for child
  notes?: string; // Only for child - includes allergies, dietary restrictions, etc.
  phone?: string; // Only for adult
  saveAsProfile?: boolean; // TODO: not implemented yet
}

interface TemporaryParticipantFormProps {
  type: 'child' | 'adult';
  onAdd: (data: TemporaryParticipantData) => Promise<void>;
  onCancel: () => void;
}

/**
 * Form for adding a temporary participant (not saved to profile)
 * Supports both child and adult participant types
 */
export default function TemporaryParticipantForm({
  type,
  onAdd,
  onCancel,
}: TemporaryParticipantFormProps) {
  const { t } = useTranslation(['booking']);

  const [formData, setFormData] = useState<TemporaryParticipantData>({
    name: '',
    preferredName: '',
    birthYear: '',
    birthMonth: '',
    notes: '',
    phone: '',
    saveAsProfile: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: keyof TemporaryParticipantData, value: string | boolean) => {
    setFormData({ ...formData, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t(`booking:checkout.${type}Participants.fields.name`) + ' is required';
    }

    if (type === 'child') {
      if (!formData.preferredName?.trim()) {
        newErrors.preferredName =
          t('booking:checkout.childParticipants.fields.preferredName') + ' is required';
      }
      if (!formData.birthYear) {
        newErrors.birthYear =
          t('booking:checkout.childParticipants.fields.selectYear') + ' is required';
      }
      if (!formData.birthMonth) {
        newErrors.birthMonth =
          t('booking:checkout.childParticipants.fields.selectMonth') + ' is required';
      }
    } else {
      if (!formData.phone?.trim()) {
        newErrors.phone = t('booking:checkout.adultParticipants.fields.phone') + ' is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Clean up data based on type
    const cleanedData: TemporaryParticipantData = {
      name: formData.name,
      saveAsProfile: formData.saveAsProfile,
    };

    if (type === 'child') {
      cleanedData.preferredName = formData.preferredName;
      cleanedData.birthYear = formData.birthYear;
      cleanedData.birthMonth = formData.birthMonth;
      cleanedData.notes = formData.notes;
    } else {
      cleanedData.phone = formData.phone;
    }

    try {
      setIsSubmitting(true);
      await onAdd(cleanedData);
    } catch (error) {
      console.error('Failed to add participant:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const typeLabel =
    type === 'child'
      ? t('booking:checkout.childParticipants.child')
      : t('booking:checkout.adultParticipants.adult');

  return (
    <div className="p-4 border border-primary rounded-lg bg-primary-50">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-medium text-gray-900">
          {t('booking:checkout.temporaryForm.title', { type: typeLabel })}
        </h4>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="space-y-3">
        {type === 'child' ? (
          <>
            {/* Child Name (English full name) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('booking:checkout.childParticipants.fields.name')}{' '}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('booking:checkout.childParticipants.fields.namePlaceholder')}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('booking:checkout.childParticipants.fields.nameHint')}
              </p>
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>

            {/* Preferred Name (Nickname) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('booking:checkout.childParticipants.fields.preferredName')}{' '}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.preferredName}
                onChange={(e) => handleChange('preferredName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.preferredName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t(
                  'booking:checkout.childParticipants.fields.preferredNamePlaceholder'
                )}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('booking:checkout.childParticipants.fields.preferredNameHint')}
              </p>
              {errors.preferredName && (
                <p className="text-xs text-red-500 mt-1">{errors.preferredName}</p>
              )}
            </div>

            {/* Birth Year & Month */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('booking:checkout.childParticipants.fields.birthDate')}{' '}
                <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <select
                    value={formData.birthYear}
                    onChange={(e) => handleChange('birthYear', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.birthYear ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">
                      {t('booking:checkout.childParticipants.fields.selectYear')}
                    </option>
                    {Array.from({ length: 18 }, (_, i) => new Date().getFullYear() - i).map(
                      (year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      )
                    )}
                  </select>
                  {errors.birthYear && (
                    <p className="text-xs text-red-500 mt-1">{errors.birthYear}</p>
                  )}
                </div>
                <div>
                  <select
                    value={formData.birthMonth}
                    onChange={(e) => handleChange('birthMonth', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.birthMonth ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">
                      {t('booking:checkout.childParticipants.fields.selectMonth')}
                    </option>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </select>
                  {errors.birthMonth && (
                    <p className="text-xs text-red-500 mt-1">{errors.birthMonth}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Special Needs (includes allergies, dietary restrictions, etc.) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('booking:checkout.childParticipants.fields.specialNeeds')}
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={3}
                placeholder={t('booking:checkout.childParticipants.fields.specialNeedsPlaceholder')}
              />
            </div>
          </>
        ) : (
          <>
            {/* Adult Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('booking:checkout.adultParticipants.fields.name')}{' '}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('booking:checkout.adultParticipants.fields.namePlaceholder')}
              />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('booking:checkout.adultParticipants.fields.phone')}{' '}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) =>
                  handleChange('phone', e.target.value.replace(/[^\d+\-()（）#转 ]/g, ''))
                }
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('booking:checkout.adultParticipants.fields.phonePlaceholder')}
              />
              {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
            </div>
          </>
        )}

        {/* Save as Profile Feature */}
        <div className="pt-2">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.saveAsProfile}
              onChange={(e) => handleChange('saveAsProfile', e.target.checked)}
              className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
            />
            {t('booking:checkout.temporaryForm.saveAsProfile')}
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            fullWidth
            disabled={isSubmitting}
          >
            {t('booking:checkout.temporaryForm.cancelButton')}
          </Button>
          <Button
            type="button"
            variant="primary"
            fullWidth
            disabled={isSubmitting}
            onClick={(e) => handleSubmit(e as unknown as React.FormEvent)}
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {formData.saveAsProfile
                  ? t('booking:checkout.temporaryForm.saving')
                  : t('booking:checkout.temporaryForm.adding')}
              </>
            ) : (
              t('booking:checkout.temporaryForm.addButton')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
